import { createContext, HTMLProps, ReactNode, useContext } from "react";

import CheckIcon from "@/assets/icons/check.svg";
import { FeatureType } from "@/features";
import useSearchParams from "@/hooks/useSearchParams";
import { FrontendPlan } from "@/mappings/plans";
import { cx } from "@/utils";
import Typography, { TypographyProps } from "@components/Typography";
import { i18n } from "@lingui/core";

import styles from "./PlanCard.module.css";
const PlanCardContext = createContext<FrontendPlan>({} as FrontendPlan);
export const usePlanCard = () => useContext(PlanCardContext);

const ListItem = ({
  active,
  ...props
}: {
  active: boolean;
} & HTMLProps<HTMLSpanElement>) => (
  <li className={cx(styles.item, { [styles.active]: active })}>
    <CheckIcon strokeWidth={3} width="1rem" />
    <span {...props} />
  </li>
);

export const Features = ({ className }: HTMLProps<HTMLUListElement>) => {
  const [{ upsale }] = useSearchParams<{ upsale?: FeatureType }>();
  const { features } = usePlanCard();

  return (
    <ul className={cx(styles.list, className)}>
      {features.map((feature, index) => {
        return (
          <ListItem
            active={
              feature.required_feature && feature.required_feature === upsale
                ? true
                : false
            }
            key={index}
            dangerouslySetInnerHTML={{ __html: i18n._(feature.name) }}
          />
        );
      })}
    </ul>
  );
};

export const Root = ({
  plan,
  children,
}: {
  plan: FrontendPlan;
  children: ReactNode;
}) => (
  <PlanCardContext.Provider value={plan}>{children}</PlanCardContext.Provider>
);

export const Price = ({ className, ...props }: TypographyProps) => {
  const { price_string, price_extra } = usePlanCard();
  return (
    <Typography
      margin="none"
      variant="h1"
      className={cx(styles.price, className)}
      {...props}
    >
      {i18n._(price_string)}
      {price_extra && (
        <Typography
          component="span"
          margin="none"
          className={styles.priceExtra}
        >
          {i18n._(price_extra)}
        </Typography>
      )}
    </Typography>
  );
};

export const Legal = (props: TypographyProps) => {
  const { price_legal } = usePlanCard();
  return (
    <Typography margin="none" variant="body-2" {...props}>
      {price_legal ? i18n._(price_legal) : null}
    </Typography>
  );
};

export const MonthlyPrice = ({ className, ...props }: TypographyProps) => {
  const { price_string_monthly } = usePlanCard();
  if (!price_string_monthly) return null;

  return (
    <Typography
      margin="none"
      variant="body-1"
      className={cx(styles.monthlyPrice, className)}
      {...props}
    >
      {i18n._(price_string_monthly)}
    </Typography>
  );
};

export const Name = ({ children, ...props }: TypographyProps) => {
  const { name, accent } = usePlanCard();
  return (
    <Typography
      style={{ color: accent }}
      className={styles.name}
      variant="h2"
      margin="none"
      {...props}
    >
      {i18n._(name)} {children}
    </Typography>
  );
};

export const Description = (props: TypographyProps) => {
  const { description } = usePlanCard();
  if (!description) return null;
  return (
    <Typography
      margin="none"
      {...props}
      dangerouslySetInnerHTML={{ __html: i18n._(description) }}
    />
  );
};

export const FeatureTitle = (props: TypographyProps) => {
  const { feature_title } = usePlanCard();
  return (
    <Typography margin="none" variant="subtitle-1" {...props}>
      {feature_title ? i18n._(feature_title) : null}
    </Typography>
  );
};
